import React from 'react'

import { EuiPanel, EuiTitle, EuiSpacer, EuiLoadingContent, EuiFlexGroup, EuiFlexItem, EuiImage, EuiEmptyPrompt } from '@elastic/eui'

import { ReportAdGroup, Campaign, CampaignType } from 'api/interfaces'
import ReportDataGrid, { ReportDataGridColumn, ReportDataGridSorting } from 'features/reports/ReportDataGrid'
import { formatCurrency, formatNumber, formatPercentage } from 'utils/Formatters'

import { EmptyPromptComponent, LoadingComponent, TitleComponent } from './FormattingComponents'

interface AdPerformanceProps {
  adPerformanceGroups?: ReportAdGroup[]
  campaign?: Campaign
  isLoading: boolean
}

const AdPerformance: React.FC<AdPerformanceProps> = ({ adPerformanceGroups, campaign, isLoading }) => {
  const title = 'Ad Data'

  if (isLoading) {
    return <LoadingComponent title={title} />
  }

  if (!adPerformanceGroups || adPerformanceGroups.length <= 0) {
    return <EmptyPromptComponent title={title} body='There is no ad data for the selected period.' />
  }

  const defaultSorting: ReportDataGridSorting[] = [
    {
      id: 'forDate',
      direction: 'desc'
    }
  ]

  const createColumns = (isInternetCampaign: boolean): ReportDataGridColumn[] => {
    const columns: ReportDataGridColumn[] = [
      {
        id: 'forDate',
        displayAsText: 'Date',
        schema: 'date'
      },
      {
        id: 'views',
        displayAsText: 'Views',
        schema: 'numeric',
        render: (value: number) => formatNumber(value, 0, 0)
      }
    ]

    if (isInternetCampaign) {
      columns.push(
        {
          id: 'clicks',
          displayAsText: 'Clicks',
          schema: 'numeric',
          render: (value: number) => formatNumber(value, 0, 0)
        },
        {
          id: 'ctr',
          displayAsText: 'CTR %',
          schema: 'numeric',
          render: (value: number) => formatPercentage(value / 100, 3, 3)
        }
      )
    }

    columns.push(
      {
        id: 'spend',
        displayAsText: 'Spend',
        schema: 'currency',
        render: (value: number) => formatCurrency(value)
      },
      {
        id: 'cpm',
        displayAsText: 'CPM',
        schema: 'currency',
        render: (value: number) => formatCurrency(value)
      }
    )

    return columns
  }

  return (
    <>
      {adPerformanceGroups.map(adGroup => {
        const adName = adGroup.adName
        const performanceData = adGroup.performanceData

        if (!adGroup.hasData || performanceData.length <= 0) {
          return (
            <React.Fragment key={adGroup.adId}>
              <EuiPanel>
                <TitleComponent title={adName} />

                <EuiSpacer size='m' />

                <EuiFlexGroup alignItems='center'>
                  <EuiFlexItem grow={false} style={{ width: '600px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '450px'
                      }}>
                      {adGroup.adImageUrl && (
                        <EuiImage
                          size='original'
                          alt={adGroup.adHeadline || 'Ad creative'}
                          url={adGroup.adImageUrl}
                          style={{
                            maxWidth: '540px',
                            maxHeight: '390px',
                            objectFit: 'contain'
                          }}
                        />
                      )}
                      {adGroup.adVideoUrl && !adGroup.adImageUrl && (
                        <video
                          width='540'
                          height='390'
                          style={{
                            objectFit: 'contain',
                            maxWidth: '540px',
                            maxHeight: '390px'
                          }}
                          preload='metadata'
                          controls>
                          <source src={adGroup.adVideoUrl} type='video/mp4' />
                        </video>
                      )}
                      {!adGroup.adImageUrl && !adGroup.adVideoUrl && (
                        <div
                          style={{
                            color: '#69707D',
                            textAlign: 'center',
                            fontSize: '14px'
                          }}>
                          No preview available
                        </div>
                      )}
                    </div>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiEmptyPrompt title={<h3>No data available</h3>} body='There is no performance data for this ad in the selected period.' />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiPanel>
              <EuiSpacer size='l' />
            </React.Fragment>
          )
        }

        const isInternetCampaign = campaign?.type === CampaignType.Internet
        const columns = createColumns(isInternetCampaign)

        return (
          <React.Fragment key={adGroup.adId}>
            <EuiPanel>
              <EuiTitle size='m'>
                <h2>{adName}</h2>
              </EuiTitle>
              <EuiSpacer size='m' />

              <EuiFlexGroup alignItems='center'>
                <EuiFlexItem grow={false} style={{ width: '600px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '450px'
                    }}>
                    {adGroup.adImageUrl && (
                      <EuiImage
                        size='original'
                        alt={adGroup.adHeadline || 'Ad creative'}
                        url={adGroup.adImageUrl}
                        style={{
                          maxWidth: '540px',
                          maxHeight: '390px',
                          objectFit: 'contain'
                        }}
                      />
                    )}
                    {adGroup.adVideoUrl && !adGroup.adImageUrl && (
                      <video
                        width='540'
                        height='390'
                        style={{
                          objectFit: 'contain',
                          maxWidth: '540px',
                          maxHeight: '390px'
                        }}
                        preload='metadata'
                        controls>
                        <source src={adGroup.adVideoUrl} type='video/mp4' />
                      </video>
                    )}
                    {!adGroup.adImageUrl && !adGroup.adVideoUrl && (
                      <div
                        style={{
                          color: '#69707D',
                          textAlign: 'center',
                          fontSize: '14px'
                        }}>
                        No preview available
                      </div>
                    )}
                  </div>
                </EuiFlexItem>

                <EuiFlexItem>
                  <ReportDataGrid data={performanceData} columns={columns} defaultSorting={defaultSorting} />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiPanel>
            <EuiSpacer size='l' />
          </React.Fragment>
        )
      })}
    </>
  )
}

export default AdPerformance
